/**
 * Acceso con Google mediante Google Identity Services (GIS).
 *
 * Se usa en lugar de `signInWithPopup` / `signInWithRedirect` de Firebase
 * porque esos dos flujos pasan por el `authDomain` del proyecto
 * (`reconstruccion-net.firebaseapp.com`), que es un tercero respecto al sitio
 * donde vive el portal. Los navegadores actuales bloquean el almacenamiento de
 * terceros, así que Google autenticaba bien pero la sesión se perdía al
 * volver, sin error visible.
 *
 * GIS entrega el token de identidad directamente a esta página —sin
 * redirección, sin iframe y sin cookies de terceros— y ese token se canjea con
 * Firebase mediante `signInWithCredential`. Es el mismo mecanismo que usa la
 * app móvil (ver recnet-24 `config/googleSignInConfig.js`).
 */

const GSI_SRC = "https://accounts.google.com/gsi/client";

/**
 * Client ID web del proyecto `reconstruccion-net`, tomado de
 * `google-services.json` de la app. Es público por diseño: viaja al navegador
 * en cualquier caso. Tenerlo aquí es orden, no seguridad.
 */
const CLIENT_ID_POR_DEFECTO =
  "222985236513-vm362mnjo9igd1labpuds4r2en0soun7.apps.googleusercontent.com";

export const GOOGLE_CLIENT_ID =
  process.env.VUE_APP_GOOGLE_CLIENT_ID || CLIENT_ID_POR_DEFECTO;

let promesaScript = null;

/** Carga el script de GIS una sola vez, aunque lo pidan varias vistas. */
export function cargarGoogleIdentity() {
  if (window.google && window.google.accounts && window.google.accounts.id) {
    return Promise.resolve(window.google);
  }
  if (promesaScript) return promesaScript;

  promesaScript = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google && window.google.accounts && window.google.accounts.id) {
        resolve(window.google);
      } else {
        promesaScript = null;
        reject(new Error("El script de Google cargó pero no expuso su API."));
      }
    };
    script.onerror = () => {
      // Se limpia para que un segundo intento vuelva a probar: puede haber sido
      // un corte de red pasajero.
      promesaScript = null;
      reject(new Error("No se pudo cargar el acceso con Google."));
    };
    document.head.appendChild(script);
  });

  return promesaScript;
}

/**
 * Dibuja el botón oficial de Google dentro de `contenedor`.
 *
 * @param {HTMLElement} contenedor
 * @param {(idToken: string) => void} alRecibirCredencial
 */
export async function renderizarBotonGoogle(contenedor, alRecibirCredencial) {
  const google = await cargarGoogleIdentity();

  google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: (respuesta) => {
      if (respuesta && respuesta.credential) alRecibirCredencial(respuesta.credential);
    },
    // Sin selección automática: el usuario decide con qué cuenta entra, y así
    // no se le mete en una sesión que no pidió.
    auto_select: false,
    cancel_on_tap_outside: true,
  });

  // GIS exige un ancho numérico en píxeles y lo topa en 400.
  const ancho = Math.min(Math.round(contenedor.offsetWidth) || 320, 400);

  google.accounts.id.renderButton(contenedor, {
    type: "standard",
    theme: "outline",
    size: "large",
    text: "continue_with",
    shape: "rectangular",
    logo_alignment: "center",
    locale: "es",
    width: ancho,
  });
}
